// =============================================================================
// Project:        Nifty Finance
// Filename:       firebase.js
// Description:    Firebase client initialization. Exports app-wide Firestore
//                 and Auth instances for use throughout the React app. Uses
//                 environment variables for credentials; do not hardcode secrets.
//
// Copyright (c) 2025 Nifty Finance. All rights reserved.
//
// License:        Proprietary and confidential. Unauthorized copying,
//                 distribution, modification, or use, in whole or in part,
//                 is strictly prohibited. For internal business use only.
//                 Contact hello@niftyfinance.com for licensing inquiries.
//
// Contact:        hello@niftyfinance.com | https://niftyfinance.com
//
// -----------------------------------------------------------------------------
// Developer Context:
// -----------------------------------------------------------------------------
// - Centralizes Firebase SDK setup and exports configured instances.
// - Do NOT commit .env or credentials. All keys must be set in environment.
// - `auth` exported from getAuth(app) for use with Firebase Authentication.
// - `db` exported from getFirestore(app) for Firestore queries/writes.
// - Update config if Firebase project or environment keys change.
// - No business logic. Do not modify unless upgrading SDK or changing config.
//
// -----------------------------------------------------------------------------
// Changelog:
// -----------------------------------------------------------------------------
// Date      | Version     | Author                      | Changes
// -----------|---------|----------------------|--------------------------------------
// 2025-07-05 | 1.0.0   | Trevor Bissonette    | Initial production version.
//
// -----------------------------------------------------------------------------
// Version Control:
// -----------------------------------------------------------------------------
// Version     : 1.0.0
// Last Update : 2025-07-05
// Repo        : https://github.com/NiftyFinance/Nifty
// =============================================================================




// src/firebase.js
import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';

const firebaseConfig = {
  apiKey:   process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:  process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:    process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db   = getFirestore(app);
export const functions = getFunctions(app, 'us-central1');

if (
  process.env.NODE_ENV !== 'production'
  && process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true'
  && !window.__NIFTY_FIREBASE_EMULATORS_CONNECTED__
) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  window.__NIFTY_FIREBASE_EMULATORS_CONNECTED__ = true;
}
